import { CompetitionGroup, EventName, FilterGroup, RankingCategory } from './types';

export const maxSimilarMarks: { [k in EventName]?: number } = {
  '800m': 2,
  '1500m': 2,
  '5000m': 1,
  '10000m': 0,
};

// pg 3 https://www.worldathletics.org/download/download?filename=52dcc850-1eb9-47e7-805a-0617b3db9191.pdf&urlSlug=world-athletics-championships-oregon22-qual
export const fieldSizes: { [k in EventName]?: number } = {
  '100m': 48,
  '200m': 56,
  '400m': 48,
  '800m': 48,
  '1500m': 45,
  '5000m': 42,
  '10000m': 27,
};

// 2.7 https://www.worldathletics.org/world-ranking-rules/track-field-events
export const perfsToAverage: { [k in EventName]?: number } = {
  '100m': 5,
  '200m': 5,
  '400m': 5,
  '800m': 5,
  '1500m': 5,
  '5000m': 3,
  '10000m': 2,
};

export const evtDistance: { [k in EventName]: number } = {
  '100m': 100,
  '200m': 200,
  '400m': 400,
  '600m': 600,
  '800m': 800,
  '1000m': 1000,
  '1500m': 1500,
  Mile: 1609.344,
  'Mile Road Race': 1609.344,
  '2000m': 2000,
  '3000m': 3000,
  '2 miles': 1609.344 * 2,
  '5000m': 5000,
  '5km Road Race': 5000,
  '10000m': 10000,
  '10km Road Race': 10000,
};

// https://www.worldathletics.org/world-ranking-rules/track-field-events
export const similarEvents: { [k in EventName]?: EventName[] } = {
  '800m': ['600m', '1000m'],
  '1500m': ['Mile', 'Mile Road Race', '2000m'],
  '5000m': ['3000m', '2 miles', '5km Road Race'],
  '10000m': ['10km Road Race'],
};

export const waCalculatorDisciplines: { [k in EventName]?: string } = {
  Mile: '1mile',
  '2 miles': '2miles',
  '5km Road Race': '5km',
  '10km Road Race': '10km',
};

const defaultPlaceScoresFinal = {
  OW: [375, 330, 300, 270, 250, 230, 215, 200, 130, 120, 110, 100, 95, 90, 85, 80],
  DF: [240, 210, 185, 170, 155, 145, 135, 125, 90, 80, 70, 60],
  GW: [200, 170, 150, 140, 130, 120, 110, 100, 70, 60, 50, 45],
  GL: [170, 145, 130, 120, 110, 100, 90, 80, 60, 50, 45, 40],
  A: [140, 120, 110, 100, 90, 80, 70, 60],
  B: [100, 80, 70, 60, 55, 50, 45, 40],
  C: [60, 50, 45, 40, 35, 30, 27, 25],
  D: [40, 35, 30, 25, 22, 19, 17, 15],
  E: [25, 21, 18, 15, 12, 10],
  F: [15, 10, 5],
};

const steeple5kPlaceScoresFinal = {
  OW: [305, 270, 240, 220, 200, 185, 175, 165, 110, 100, 90, 80, 75, 70, 65, 60],
  DF: [210, 185, 160, 145, 130, 120, 110, 100, 70, 60, 55, 50],
  GW: [180, 160, 140, 120, 110, 100, 90, 80, 55, 45, 40, 35],
  GL: [150, 135, 120, 105, 90, 80, 70, 60, 45, 40, 35, 30],
  A: [100, 90, 80, 70, 60, 50, 45, 40],
  B: [70, 60, 50, 45, 40, 35, 30, 25],
  C: [50, 40, 35, 30, 26, 23, 20, 18],
  D: [35, 27, 22, 19, 16, 14, 12, 10],
  E: [20, 16, 12, 10, 9, 8],
  F: [12, 7, 4],
};

export const placeScoresFinal: { [k in EventName]?: { [k in RankingCategory]: number[] } } = {
  '600m': defaultPlaceScoresFinal,
  '800m': defaultPlaceScoresFinal,
  '1000m': defaultPlaceScoresFinal,
  '1500m': defaultPlaceScoresFinal,
  Mile: defaultPlaceScoresFinal,
  'Mile Road Race': defaultPlaceScoresFinal,
  '2000m': defaultPlaceScoresFinal,
  '3000m': defaultPlaceScoresFinal,
  '2 miles': defaultPlaceScoresFinal,
  '5000m': steeple5kPlaceScoresFinal,
  '5km Road Race': steeple5kPlaceScoresFinal,
  '10000m': {
    OW: [280, 250, 225, 205, 185, 170, 155, 145, 95, 85, 75, 65, 60, 55, 50, 40],
    DF: [175, 150, 135, 120, 105, 95, 85, 75, 50, 40, 35, 30],
    GW: [140, 120, 105, 90, 80, 70, 60, 50, 40, 32, 27, 24],
    GL: [110, 90, 75, 65, 55, 50, 45, 40, 30, 25, 22, 20],
    A: [80, 70, 60, 50, 45, 40, 35, 30],
    B: [60, 50, 45, 40, 35, 30, 25, 20],
    C: [45, 38, 32, 26, 22, 19, 17, 15],
    D: [30, 22, 18, 16, 14, 12, 11, 10],
    E: [20, 14, 10, 8, 7, 6],
    F: [10, 6, 3],
  },
  '10km Road Race': {
    OW: [95, 85, 75, 65, 55, 50, 45, 40, 35, 30, 25, 20],
    DF: [70, 60, 50, 45, 40, 35, 30, 25],
    GW: [60, 50, 45, 40, 35, 30, 25, 20],
    GL: [45, 38, 32, 26, 22, 19, 17, 15],
    A: [30, 22, 18, 16, 14, 12, 11, 10],
    B: [20, 14, 20, 8, 7, 6],
    C: [10, 6, 3],
    D: [],
    E: [],
    F: [],
  },
};

// TODO needs to be revised
export const placeScoresHeatsFor10PlusFinal = {
  OW: [100, 100, 100, 100, 100, 100, 100, 100, 100, 100, 95, 90, 85, 80, 75, 70],
  DF: [65, 65, 65, 65, 65, 65, 65, 65, 65, 65, 55, 50],
  GW: [50, 50, 50, 50, 50, 50, 50, 50, 50, 50, 40, 35],
  GL: [40, 40, 40, 40, 40, 40, 40, 40, 40, 40, 35, 30],
};

export const filterGroups: { [k in FilterGroup]: (CompetitionGroup | number)[] } = {
  'Area Championships': [
    'Area Indoor Championships',
    'Other Senior Area Championships',
    'Area Senior Regional Championships',
    'Area Senior Outdoor Championships',
    'Area Senior CE Championships',
    'Area Senior Race Walking Championships',
    'Area RR Championships',
    'Area Marathon Championships',
    7171137,
    7147621,
    7149591,
    7174918,
    7176005,
    7176009,
    7179813,
    7180027,
  ],
  'National Championships': [
    'National Senior Outdoor Championships',
    'National Senior Indoor Championships',
    'National Senior Outdoor Combined Events Championships',
    'National Senior Road Running Championships',
    'National Senior 10,000m Championships',
    'National Senior Half Marathon Championships',
    'National Senior Marathon Championships',
    'National Senior Outdoor Race Walking Championships',
    'National Senior Indoor Combined Events Championships',
    7172099,
    7172332,
    7172692,
    7173083,
    7173090,
    7173101,
    7173106,
    7173107,
    7175035,
    7187091,
    7187328,
    7186865,
    7170157,
    7173141,
    7173872,
    7174151,
    7174321,
    7174419,
    7174433,
    7174442,
    7174546,
    7174764,
    7174786,
    7174822,
    7174823,
    7174914,
    7175196,
    7175312,
    7175459,
    7175516,
    7175721,
    7175754,
    7175822,
    7175838,
    7175865,
    7175898,
    7176032,
    7176037,
    7176039,
    7176053,
    7176054,
    7176135,
    7176137,
    7176171,
    7176204,
    7176985,
    7177106,
    7177483,
    7177780,
    7177940,
    7178631,
    7178840,
    7178961,
    7179000,
    7179026,
    7179102,
    7179141,
    7179224,
    7179309,
    7179498,
    7179500,
    7179521,
    7179520,
    7179522,
    7179523,
    7179614,
    7179615,
    7179674,
    7179679,
    7179703,
    7179723,
    7179810,
    7179815, // typo
    7179816,
    7179822,
    7179911,
    7180050,
    7180069,
    7180135,
    7180136,
    7180151,
    7180163,
    7180172,
    7180185,
    7180236,
    7180241,
    7180245,
    7180416,
    7180432,
    7180574,
    7180604,
    7181339,
    7181368,
    7181406,
    7181699,
    7181834,
    7182288,
    7182388,
    7182457,
    7182831,
    7182926,
    7183333,
    7183379,
    7183381,
    7183954,
    7184239,
    7184510,
    7184646,
    7184906,
    7185726,
    7185863,
    7186148,
    7186560,
    7181802,
  ],
  'National U18 Championships': [7173137],
  'Area U23 Championships': ['Area U23 Championships', 7184735],
  'Area U20 Championships': ['Area U20 Championships', 7183153],
  'Area U18 Championships': ['Area U18 Championships', 7158240, 7184760],
  'NCAA Championships': [7155001, 7155004],
  'DL Final': [7147617],
};

// cat public/results/2021-07-14_2022-06-26_simplified.json | jq -r '.results[].competition.venue' | rev | cut -c 2-4 | sort | uniq | rev
export const countryCodes = [
  'ALB',
  'ALG',
  'ARG',
  'AUS',
  'AUT',
  'AZE',
  'BAH',
  'BAN',
  'BEL',
  'BER',
  'BIH',
  'BLR',
  'BOL',
  'BOT',
  'BRA',
  'BRN',
  'BUL',
  'CAN',
  'CHI',
  'CHN',
  'CMR',
  'COD',
  'COL',
  'CRC',
  'CRO',
  'CUB',
  'CYP',
  'CZE',
  'DEN',
  'DOM',
  'ECU',
  'EGY',
  'ERI',
  'ESA',
  'ESP',
  'EST',
  'ETH',
  'FIN',
  'FRA',
  'GAB',
  'GBR',
  'GEO',
  'GER',
  'GRE',
  'GRN',
  'GUA',
  'GUY',
  'HKG',
  'HUN',
  'IND',
  'IRI',
  'IRL',
  'IRQ',
  'ISL',
  'ISR',
  'ITA',
  'JAM',
  'JPN',
  'KAZ',
  'KEN',
  'KGZ',
  'KOR',
  'KSA',
  'KUW',
  'LAT',
  'LIE',
  'LTU',
  'LUX',
  'MAR',
  'MAS',
  'MDA',
  'MDV',
  'MEX',
  'MLT',
  'MNE',
  'MRI',
  'NED',
  'NEP',
  'NGR',
  'NMI',
  'NOR',
  'NZL',
  'OMA',
  'PAR',
  'PER',
  'PHI',
  'POL',
  'POR',
  'PUR',
  'QAT',
  'ROU',
  'RSA',
  'RUS',
  'RWA',
  'SEN',
  'SGP',
  'SKN',
  'SLO',
  'SRB',
  'SRI',
  'SUI',
  'SVK',
  'SWE',
  'SWZ',
  'TAN',
  'THA',
  'TJK',
  'TPE',
  'TTO',
  'TUN',
  'TUR',
  'UAE',
  'UGA',
  'UKR',
  'URU',
  'USA',
  'UZB',
  'VEN',
  'VIE',
  'VIN',
  'ZAM',
];
